import React, { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Sparkle, ArrowRight, CheckCircle, EnvelopeSimple } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LeadMagnet = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | done | error
  const [errorMsg, setErrorMsg] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus('loading');
    setErrorMsg('');
    try {
      await axios.post(`${BACKEND_URL}/api/leads/subscribe`, {
        email: email.trim(),
        name: name.trim() || null,
        source: 'landing_magnet',
      });
      setStatus('done');
    } catch (err) {
      setStatus('error');
      setErrorMsg(err?.response?.data?.detail || t('magnet.err_generic'));
    }
  };

  return (
    <section id="lead-magnet" className="px-6 py-24 border-t border-white/5 relative overflow-hidden" data-testid="lead-magnet-section">
      <div className="absolute inset-0 pointer-events-none opacity-30">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full bg-[#F59E0B]/10 blur-[150px]" />
      </div>

      <div className="relative max-w-5xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center bg-gradient-to-br from-[#F59E0B]/[0.06] via-white/[0.02] to-transparent border border-[#F59E0B]/30 rounded-3xl p-8 md:p-12">
          {/* Left: pitch */}
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#F59E0B]/15 border border-[#F59E0B]/30 rounded-full mb-5">
              <Sparkle size={14} weight="fill" className="text-[#F59E0B]" />
              <span className="text-[11px] tracking-[0.25em] uppercase text-[#F59E0B] font-semibold">{t('magnet.badge')}</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-[Outfit] font-bold tracking-tight mb-4 leading-[1.1]">
              {t('magnet.title_pre')} <span className="text-[#F59E0B]">{t('magnet.title_hl')}</span><br />
              {t('magnet.title_post')}
            </h2>
            <p className="text-white/70 mb-6 text-base md:text-lg leading-relaxed">
              {t('magnet.subtitle')}
            </p>
            <ul className="space-y-2 text-sm text-white/70 mb-2">
              <li className="flex items-start gap-2"><CheckCircle size={18} weight="fill" className="text-[#10B981] flex-shrink-0 mt-0.5" /> {t('magnet.bullet_1')}</li>
              <li className="flex items-start gap-2"><CheckCircle size={18} weight="fill" className="text-[#10B981] flex-shrink-0 mt-0.5" /> {t('magnet.bullet_2')}</li>
              <li className="flex items-start gap-2"><CheckCircle size={18} weight="fill" className="text-[#10B981] flex-shrink-0 mt-0.5" /> {t('magnet.bullet_3')}</li>
            </ul>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-5">
            {status === 'done' ? (
              <div className="bg-[#0A0A0A] border border-[#10B981]/40 rounded-2xl p-7 text-center" data-testid="lead-magnet-success">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[#10B981]/15 flex items-center justify-center">
                  <CheckCircle size={28} weight="fill" className="text-[#10B981]" />
                </div>
                <h3 className="text-xl font-[Outfit] font-bold mb-2">{t('magnet.success_title')}</h3>
                <p className="text-white/60 text-sm leading-relaxed">
                  {t('magnet.success_body')}
                  <br /><span className="text-white/40 text-xs">{t('magnet.success_spam')}</span>
                </p>
              </div>
            ) : (
              <form
                onSubmit={submit}
                className="bg-[#0A0A0A] border border-white/10 rounded-2xl p-7 space-y-4"
                data-testid="lead-magnet-form"
              >
                <div>
                  <label className="block text-xs uppercase tracking-[0.2em] text-white/40 mb-2">{t('magnet.name_label')}</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t('magnet.name_ph')}
                    data-testid="lead-magnet-name-input"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#F59E0B]/50 transition"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.2em] text-white/40 mb-2">{t('magnet.email_label')}</label>
                  <div className="relative">
                    <EnvelopeSimple size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t('magnet.email_ph')}
                      data-testid="lead-magnet-email-input"
                      className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#F59E0B]/50 transition"
                    />
                  </div>
                </div>
                {errorMsg && (
                  <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2" data-testid="lead-magnet-error">
                    {errorMsg}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={status === 'loading' || !email}
                  data-testid="lead-magnet-submit-btn"
                  className="w-full px-5 py-3.5 bg-gradient-to-r from-[#F59E0B] to-[#FBBF24] hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl text-base font-semibold transition flex items-center justify-center gap-2 shadow-lg shadow-amber-500/20 text-black"
                >
                  {status === 'loading' ? (
                    <span>{t('magnet.sending')}</span>
                  ) : (
                    <>
                      {t('magnet.cta')}
                      <ArrowRight size={18} weight="bold" />
                    </>
                  )}
                </button>
                <p className="text-[11px] text-white/30 text-center leading-relaxed">
                  {t('magnet.disclaimer')}
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default LeadMagnet;
